<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="false"
                title="Attachment Report"
                :btnTitle="'Add New'"
                :showAddNew="false"
            />
        </div>
        <div class="my-2 px-2">
          <div class="row">
            <div class="col-12 col-sm-6 col-md-3 col-lg-3">
              <label
                  for="colFormLabel"
                  class="col-form-label"
              >
                Role
              </label>
              <v-select
                  :clearable="false"
                  placeholder="Select Role"
                  v-model="companyRole"
                  :options="companyRules"
                  label="name"
              />
            </div>
            <div class="col-12 col-sm-6 col-md-3 col-lg-3">
              <label
                  for="colFormLabel"
                  class="col-form-label"
              >
                Contact Profile
              </label>
              <AsyncSelect
                  placeholder="Contact Profile"
                  v-model="contactProfile"
                  :api-service="fetchContactProfiles"
                  :additional-query="additionalContactQuery"
                  ref="contactProfileDropdown"
                  label="name"
              />
            </div>

            <div class="col-sm-2 col-md-2 d-flex align-items-end mt-2 action-btn-setter">
              <button
                  style="min-width: 64px;"
                  @click="onClickToGo"
                  class="btn btn-primary btn-block waves-effect waves-float waves-light"
              >GO
              </button>
            </div>
          </div>

        </div>

        <div class="col-12">
          <div class="d-flex justify-content-end px-2">
            <button
                @click="exportTable"
                class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
            >
              Export
            </button>
          </div>
        </div>

        <div class="col-12 px-2 mt-2 mb-4">
          <div class="card p-2" style="min-height: 200px">
            <div class="table-responsive">
              <table class="table table-bordered">
                <thead>
                <tr>
                  <th class="text-left">Contact Name</th>
                  <th class="text-center" v-for="(category, c) in attachmentCategoryList" :key="c">
                    {{ category.name }}
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, i) in tableItems" :key="i">
                  <td class="text-left">{{ item.name + '(' + item.serial_no + ')' }}</td>
                  <td class="text-center" v-for="(category, c) in attachmentCategoryList" :key="c">
                    {{ item.attachments[category.id] ?? '' }}
                  </td>
                </tr>
                </tbody>
              </table>
              <p v-if="tableItems.length < 0" class="text-center mt-5">No data</p>
            </div>
          </div>
        </div>

      <div class="px-2 position-absolute bottom-0 mt-4">
        <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
      </div>

        <Loader v-if="loading"/>

    </div>

</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import {inject, onMounted, ref, watch} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import { computed } from 'vue';
import handleContact from '@/services/modules/contact'
import handleRole from "@/services/modules/role";
import handleDropdowns from "@/services/modules/dropdowns";
import Pagination from '@/components/atom/Pagination'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const contactProfileId = ref('');
const companyRole = ref('');
const contactProfile = ref('');
const contactProfileDropdown = ref(null);
let contacts = ref([])
const tableItems = ref([]);
const attachmentCategoryList = ref([]);
const params = ref({
    offset: 20,
});
const companyRules = ref([])

const token = computed(() => localStorage.getItem('token'));

const additionalContactQuery = ref({});

watch(companyRole, (newRole) => {
  contactProfile.value = null;

  additionalContactQuery.value = {
    roles: newRole ? newRole.name : []
  };

  if (contactProfileDropdown.value && contactProfileDropdown.value.clear) {
    contactProfileDropdown.value.clear();
  }
});

const {
  fetchContactAttachmentReport,
} = handleHrPayroll()
const { fetchContactProfiles } = handleContact()
const {
  fetchCompanyDefaultRoles
} = handleRole();
const { fetchDropdownList } = handleDropdowns()
const onClickToGo = () => {
   getAttachmentReport();
}

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await getAttachmentReport();
}

const getAttachmentReport = async () => {
    const query = getQuery();
    loading.value = true
    tableItems.value = []

    await fetchContactAttachmentReport(query).then(res => {
        if (!res.status){
            showError(res.message)
            store.commit("resetPagination")
            return
        }

        tableItems.value = res.data.data;
        setPagination(res.data)
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    if (route.query.page) query += '&page=' + route.query.page;
    if (companyRole.value) query += '&role=' + companyRole.value.value
    if (contactProfile.value) query += '&contact_profile_id=' + contactProfile.value.id
    query += '&offset=' + params.value.offset
    return query;
}

const exportTable = () => {
  let query = getQuery();
  let url = `${process.env.VUE_APP_BASE_URL}/api/hr-payroll/contact-attachment-report${query}&_token=${token.value}&is_export=1&is_all=1`;
  let a = document.createElement("a");
  a.setAttribute("href", url);
  a.click();
};

const getDropdownList = async () => {
  let query = getQuery();
  query += '&is_role=1&is_attachment_category=1'

  companyRules.value = []
  attachmentCategoryList.value = []

  await fetchDropdownList(query).then(res => {
    if (!res.status){
      return
    }

    let data = res.data;
    companyRules.value = data.company_role_list;
    attachmentCategoryList.value = data.attachment_category_list;
  }).catch(err => {
    console.log(err)
  })
}

onMounted(() => {
    Promise.all([
      getDropdownList()
    ])
})
</script>
